import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = "https://luxury-wellness-shop.emergent.host";
const BRAND = "The Recovery Shops";

// Unique title + meta description for each static route.
const ROUTES = {
  "/": {
    title: `${BRAND} | Luxury Saunas, Cold Plunges & Hot Tubs`,
    description:
      "Design your elite recovery space with premium saunas, cold plunges and hot tubs. Get a personalised quote and white-glove delivery from The Recovery Shops.",
  },
  "/shop": {
    title: `Shop All | Premium Saunas, Cold Plunges & Hot Tubs | ${BRAND}`,
    description:
      "Browse our full collection of luxury saunas, cold plunges, hot tubs and accessories from the world's finest wellness brands. Request a tailored quote today.",
  },
  "/shop/sauna": {
    title: `Saunas | Infrared, Traditional & Barrel Saunas | ${BRAND}`,
    description:
      "Explore infrared, traditional, barrel and cabin saunas for home and commercial spaces. Expert-matched heaters and white-glove installation available.",
  },
  "/shop/cold-plunge": {
    title: `Cold Plunges | Ice Baths & Cold Therapy | ${BRAND}`,
    description:
      "Premium cold plunges and ice baths for contrast therapy and recovery. Explore luxury cold-water tubs and request pricing from The Recovery Shops.",
  },
  "/shop/hot-tub": {
    title: `Hot Tubs | Luxury Soaking & Hydrotherapy | ${BRAND}`,
    description:
      "Discover luxury hot tubs and spas for relaxation and hydrotherapy. Premium builds, white-glove delivery and personalised quotes from The Recovery Shops.",
  },
  "/shop/accessory": {
    title: `Sauna Accessories & Heaters | ${BRAND}`,
    description:
      "Shop sauna heaters, stones, controls, lighting, salt walls, oils and comfort accessories to complete your recovery setup. Compatibility-matched by our experts.",
  },
  "/quiz": {
    title: `The Recovery Builder | Design Your Custom Recovery Setup | ${BRAND}`,
    description:
      "Answer a few questions and our Recovery Builder designs your ideal sauna, cold plunge and hot tub setup — matched to your space, style and goals.",
  },
  "/commercial": {
    title: `Commercial & Trade | Wellness Solutions for Gyms, Spas & Hotels | ${BRAND}`,
    description:
      "Commercial-grade saunas, cold plunges and hot tubs for gyms, spas, hotels and Airbnbs. Volume pricing, dedicated support and installation. Request a quote.",
  },
  "/about": {
    title: `About Us | ${BRAND}`,
    description:
      "The Recovery Shops curates the world's finest recovery equipment and delivers white-glove service for elite home and commercial wellness spaces.",
  },
  "/faq": {
    title: `FAQ | Saunas, Cold Plunges, Delivery & Installation | ${BRAND}`,
    description:
      "Answers on white-glove delivery, installation, warranties, returns, pricing and commercial orders for saunas, cold plunges and hot tubs.",
  },
  "/contact": {
    title: `Contact Us | ${BRAND}`,
    description:
      "Get in touch with The Recovery Shops for personalised quotes, product advice and white-glove delivery. Call or email our recovery specialists today.",
  },
  "/guides": {
    title: `Recovery Guides & Buying Advice | ${BRAND}`,
    description:
      "Expert guides on choosing saunas, cold plunges and hot tubs — heat types, sizing, placement, contrast therapy and getting the most from your recovery space.",
  },
  "/brands": {
    title: `Shop by Brand | Harvia, Saunum, Kohler & More | ${BRAND}`,
    description:
      "Explore premium recovery brands including Harvia, Saunum, Kohler, SaunaLife, Finnmark, Dundalk LeisureCraft and more at The Recovery Shops.",
  },
};

export function Seo({ title, description }) {
  const t = title ? `${title}` : BRAND;
  const d =
    description ||
    "Premium saunas, cold plunges and hot tubs with white-glove delivery. Design your elite recovery space with The Recovery Shops.";
  const { pathname } = useLocation();
  return (
    <Helmet>
      <title>{t}</title>
      <meta name="description" content={d} />
      <link rel="canonical" href={`${SITE}${pathname}`} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE}${pathname}`} />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
  );
}

// Sets SEO tags for known static routes only. Dynamic pages (product, collection,
// brand, policy) render their own <Seo>, so RouteSeo returns null for them to
// avoid duplicate tags.
export function RouteSeo() {
  const { pathname } = useLocation();
  const match = ROUTES[pathname];
  if (!match) return null;
  return <Seo title={match.title} description={match.description} />;
}
